"use client";

import { useState, useEffect } from "react";
import { Mic, Monitor, AudioLines } from "lucide-react";
import Label from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";
import MicInputMeter from "./mic-input-meter";
import { detectCaptureEnvironment, getShareAudioGuidance } from "../../utils/captureEnvironment";

/** @typedef {'input' | 'output' | 'both'} AudioSourceMode */

/**
 * @param {Object} props
 * @param {AudioSourceMode} props.audioSourceMode
 * @param {(mode: AudioSourceMode) => void} props.onAudioSourceModeChange
 * @param {string} [props.audioInputDeviceId]
 * @param {(deviceId: string) => void} props.onAudioInputDeviceChange
 * @param {() => Promise<Array<{ deviceId: string; label: string; kind: string }>>} props.getAudioInputDevices
 * @param {string} [props.className]
 */
export default function RecordingOptions({
  audioSourceMode,
  onAudioSourceModeChange,
  audioInputDeviceId,
  onAudioInputDeviceChange,
  getAudioInputDevices,
  className,
}) {
  const [devices, setDevices] = useState([]);
  const shareGuidance = getShareAudioGuidance(detectCaptureEnvironment());

  useEffect(() => {
    let cancelled = false;
    getAudioInputDevices?.().then((list) => {
      if (!cancelled) setDevices(list);
    });
    return () => { cancelled = true; };
  }, [getAudioInputDevices]);

  const showInputDevice = audioSourceMode === "input" || audioSourceMode === "both";

  return (
    <div className={cn("space-y-4 px-3", className)} data-tour="recording-options-menu">
      <div className="space-y-3">
        <Label className="text-sm font-medium">Recording source</Label>
        <RadioGroup
          value={audioSourceMode}
          onValueChange={(v) => onAudioSourceModeChange(v)}
          className="flex flex-wrap gap-4"
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="input" id="source-input" />
            <Mic className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">In-office Visit</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="both" id="source-both" />
            <AudioLines className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Telehealth Visit</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="output" id="source-output" />
            <Monitor className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Computer Audio Only</span>
          </label>
        </RadioGroup>
      </div>

      {showInputDevice && devices.length === 0 && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <p className="mb-2">Microphone access is required for recording.</p>
          <button
            type="button"
            onClick={() => {
              getAudioInputDevices?.({ requestPermission: true }).then((list) => {
                if (list?.length) setDevices(list);
              });
            }}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 shadow-sm shadow-blue-200"
          >
            <Mic className="h-3.5 w-3.5" />
            Allow microphone access
          </button>
        </div>
      )}

      {showInputDevice && devices.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="input-device" className="text-sm font-medium">
            Microphone
          </Label>
          <Select
            value={audioInputDeviceId || (devices[0]?.deviceId ?? "")}
            onValueChange={onAudioInputDeviceChange}
          >
            <SelectTrigger id="input-device" className="w-full max-w-xs">
              <SelectValue placeholder="Select microphone" />
            </SelectTrigger>
            <SelectContent>
              {devices.map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <MicInputMeter
            deviceId={audioInputDeviceId || devices[0]?.deviceId}
            active={showInputDevice}
          />
        </div>
      )}

      {audioSourceMode === "output" && (
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-medium text-gray-700">To capture music or system audio:</p>
          <ul className="list-disc list-inside pl-1 space-y-0.5">
            {shareGuidance === "mobile" ? (
              <li>Share the screen and turn on audio if the dialog offers it. Otherwise use the microphone.</li>
            ) : shareGuidance === "firefox" ? (
              <li><strong>Firefox:</strong> Choose Entire Screen or a Window, and turn on &quot;Share audio&quot;.</li>
            ) : shareGuidance === "safari" || shareGuidance === "standard" ? (
              <li>Choose Entire Screen or a Window, and turn on the audio option in the share dialog.</li>
            ) : (
              <>
                <li><strong>Screen:</strong> Choose &quot;Entire screen&quot; and turn on &quot;Share system audio&quot;.</li>
                <li><strong>Window:</strong> Choose a window. Chrome and Edge include system audio for that share.</li>
                <li><strong>Tab:</strong> Share the tab where audio plays and turn on &quot;Share tab audio&quot;.</li>
              </>
            )}
          </ul>
          <p className="pt-1 text-muted-foreground">Computer-audio-only is recorded; live transcription runs after you upload the recording.</p>
        </div>
      )}
      {audioSourceMode === "both" && (
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-medium text-gray-700">To capture computer audio + mic:</p>
          <ul className="list-disc list-inside pl-1 space-y-0.5">
            {shareGuidance === "mobile" ? (
              <li>Your microphone is recorded. Screen audio is added when the share dialog can provide it.</li>
            ) : shareGuidance === "firefox" ? (
              <li><strong>Firefox:</strong> Your microphone is recorded with the share. Choose Entire Screen or a Window and turn on &quot;Share audio&quot; to add computer audio.</li>
            ) : shareGuidance === "safari" || shareGuidance === "standard" ? (
              <li>Your microphone is recorded with the share. Choose Entire Screen or a Window and turn on audio to add computer audio.</li>
            ) : (
              <>
                <li><strong>Screen or window:</strong> Share Entire Screen with &quot;Share system audio&quot; on, or share a window.</li>
                <li><strong>Tab:</strong> Share a tab with &quot;Share tab audio&quot; on.</li>
                <li>Your selected microphone is recorded together with the computer audio.</li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
